/**
 * Hand-rolled Laplacian-variance blur heuristic for survey image uploads.
 * Lower variance => flatter Laplacian response => probably blurry.
 * Tuned empirically; the threshold can be revisited from Mixpanel data.
 */

export const BLUR_VARIANCE_THRESHOLD =
  Number(String(process.env.REACT_APP_BLUR_VARIANCE_THRESHOLD)) || 150;
export const BLUR_DETECTION_MAX_DIMENSION = 512;

export interface IBlurDetectionResult {
  variance: number;
  isBlurry: boolean;
}

interface IGrayscaleImage {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

export function computeLaplacianVariance(image: IGrayscaleImage): number {
  const { data, width, height } = image;
  if (width < 3 || height < 3) return 0;

  const luma = new Float32Array(width * height);
  for (let i = 0, p = 0; i < data.length; i += 4, p += 1) {
    luma[p] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
  }

  const interior = (width - 2) * (height - 2);
  const response = new Float32Array(interior);
  let sum = 0;
  let idx = 0;

  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const center = luma[y * width + x];
      const top = luma[(y - 1) * width + x];
      const bottom = luma[(y + 1) * width + x];
      const left = luma[y * width + (x - 1)];
      const right = luma[y * width + (x + 1)];
      const value = top + bottom + left + right - 4 * center;
      response[idx] = value;
      sum += value;
      idx += 1;
    }
  }

  const mean = sum / interior;
  let variance = 0;
  for (let i = 0; i < interior; i += 1) {
    const diff = response[i] - mean;
    variance += diff * diff;
  }
  return variance / interior;
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to decode image"));
    };
    img.src = url;
  });
}

function drawDownsampled(img: HTMLImageElement): ImageData | null {
  const longest = Math.max(img.naturalWidth, img.naturalHeight);
  const scale =
    longest > BLUR_DETECTION_MAX_DIMENSION
      ? BLUR_DETECTION_MAX_DIMENSION / longest
      : 1;
  const width = Math.max(1, Math.round(img.naturalWidth * scale));
  const height = Math.max(1, Math.round(img.naturalHeight * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  ctx.drawImage(img, 0, 0, width, height);
  return ctx.getImageData(0, 0, width, height);
}

export async function detectBlur(file: File): Promise<IBlurDetectionResult> {
  try {
    const img = await loadImage(file);
    const imageData = drawDownsampled(img);
    if (!imageData) return { variance: NaN, isBlurry: false };
    const variance = computeLaplacianVariance(imageData);
    return { variance, isBlurry: variance < BLUR_VARIANCE_THRESHOLD };
  } catch {
    return { variance: NaN, isBlurry: false };
  }
}
